/* eslint-disable prefer-destructuring */
import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  Snackbar,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardActionArea,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { Alert, Skeleton } from '@mui/lab/';
import { yellow, green, red, blue } from '@mui/material/colors';
import moment from 'moment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ForumIcon from '@mui/icons-material/Forum';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Refresh';

import Axios, { Canceler } from 'axios';
import { HelpDeskSearchProps, OrderProp } from '../search';
import ProfileTooltip from '../../../components/ProfileTooltip';
import InputSearchAsync, { ValueProps } from '../../../components/inputs/InputSearchAsync';
import { Helpdesk, HelpdeskData } from '../dashboard';
import api from '../../../services/api';
import styles from './styles';
import Comment from '../../../components/Comment';
import { avatarUrl } from '../../../utils/functions';

export interface HelpdeskAttendancePageProps {
  darkMode?: boolean;
  search?: HelpDeskSearchProps;
}

const ALLOWED_SITUATIONS = [
  { value: '0', text: 'Pendente' },
  { value: '1', text: 'Em andamento' },
];

const DELAY_OPTIONS = [
  { value: 0, text: 'Todos' },
  { value: 1, text: 'Atrasados' },
  { value: 2, text: 'No prazo' },
];

const getInitialSituations = () => {
  const defaultSit = [
    { id: '0', value: '0', text: 'Pendente' },
    { id: '1', value: '1', text: 'Em andamento' },
  ];
  const stored = localStorage.getItem('search-helpdesk-attendance-situation');
  if (!stored) return defaultSit;

  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return defaultSit;
    if (parsed.length > 0 && typeof parsed[0] === 'string') {
      return parsed.map((v: string) => ({
        id: v,
        value: v,
        text: ALLOWED_SITUATIONS.find(s => s.value === v)?.text || v,
      }));
    }
    return parsed.length > 0 ? parsed : defaultSit;
  } catch (e) {
    return defaultSit;
  }
};

function HelpdeskSkeletonRow() {
  return (
    <Box mb={1}>
      <Skeleton variant="rectangular" width="100%" height={72} style={{ borderRadius: 4 }} />
    </Box>
  );
}

// COMPONENTE MEMOIZADO
const HelpdeskAccordionItem = memo((props: any) => {
  const { helpdesk, expanded, onToggle, isLastElement, lastElementRef, classes } = props;
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const rowId = helpdesk.id.toString();
  const post = helpdesk.posts?.[helpdesk.posts.length - 1];
  const situationBackgroundColor =
    helpdesk.situation === 0
      ? darkMode
        ? yellow[800]
        : yellow[600]
      : helpdesk.situation === 1 && helpdesk.days_delay === 0
        ? darkMode
          ? green[500]
          : green[700]
        : darkMode
          ? red[400]
          : red[700];
  const situationTextColor =
    helpdesk.situation === 0
      ? darkMode
        ? '#ffffff'
        : '#000000'
      : helpdesk.situation === 1 && helpdesk.days_delay === 0
        ? darkMode
          ? '#ffffff'
          : '#ffffff'
        : darkMode
          ? '#ffffff'
          : '#ffffff';
  const handleChange = useCallback(
    (e: React.SyntheticEvent, isExpanded: boolean) => {
      onToggle(rowId, isExpanded);
    },
    [rowId, onToggle],
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      ref={isLastElement ? lastElementRef : null}
      className={classes.lotAccordionRoot}
      style={{ borderLeft: `4px solid ${situationBackgroundColor}`, marginBottom: 4 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={classes.statusTitle} />}
        aria-controls={`${rowId}-content`}
        id={`${rowId}-header`}
      >
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={12} sm={1}>
            <Typography variant="body2" color="textSecondary">
              <Link
                to={`/helpdesk/${helpdesk.id}`}
                target="_blank"
                onClick={e => e.stopPropagation()}
                style={{ textDecoration: 'none', color: darkMode ? blue[200] : blue[700], fontWeight: 'bold' }}
              >
                #{helpdesk.id}
              </Link>
            </Typography>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ProfileTooltip userId={helpdesk.owner}>
                <img
                  style={{
                    height: 42,
                    width: 42,
                    borderRadius: '50%',
                    border: '1px solid rgba(0,0,0,0.2)',
                  }}
                  src={avatarUrl(helpdesk.owner)}
                  alt={helpdesk.owner_name}
                  onError={(e: any) => {
                    e.target.src = avatarUrl(0);
                  }}
                />
              </ProfileTooltip>

              <Box>
                <Typography variant="body2" color="textPrimary" noWrap>
                  {helpdesk.owner_name}
                </Typography>
                <Typography variant="caption" color="textSecondary" noWrap>
                  {helpdesk.owner_sector}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" color="textPrimary" noWrap style={{ fontWeight: 600 }}>
              {helpdesk.title}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="subtitle2" color="textSecondary" fontSize={11} noWrap>
              {helpdesk.target_description}
            </Typography>
            <Typography variant="body2" color="textPrimary" noWrap>
              {helpdesk.category_description}
            </Typography>
            <Typography variant="caption" color="textSecondary" noWrap>
              {helpdesk.subcategory_description}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="caption" color="textSecondary" display="block">
              Aberto em: {helpdesk.created_at_formated}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="caption" color="textSecondary" display="block">
                Vence em: {helpdesk.deadline_formated === '00/00/0000' ? '---' : helpdesk.deadline_formated}
              </Typography>
              {helpdesk.days_delay > 0 ? (
                <Typography
                  variant="caption"
                  display="block"
                  style={{ color: darkMode ? red[400] : red[700], fontWeight: 'bold' }}
                >
                  Atraso: {helpdesk.days_delay} dias
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  display="block"
                  style={{
                    color:
                      helpdesk.days_remaining < 1
                        ? darkMode
                          ? yellow[600]
                          : yellow[700]
                        : darkMode
                          ? green[200]
                          : green[600],
                    fontWeight: 'bold',
                  }}
                >
                  Vence: {helpdesk.days_remaining} dias
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Badge badgeContent={helpdesk.posts_unreaded} color="error">
                  <ForumIcon fontSize="small" color="action" />
                </Badge>
                <Typography variant="caption" color="textSecondary">
                  {helpdesk.posts?.length} Trâmite(s)
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" width={96} justifyContent="center" gap={2}>
                <Chip
                  label={helpdesk.situation_description}
                  size="small"
                  style={{ backgroundColor: situationBackgroundColor, color: situationTextColor, fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={classes.lotAccordionDetailsRoot} style={{ flexDirection: 'column' }}>
        <Grid container spacing={3} style={{ minHeight: '100%' }}>
          <Grid item xs={12} md={7}>
            <Typography variant="overline" color="textPrimary" gutterBottom>
              DESCRIÇÃO DO CHAMADO
            </Typography>
            <Box className={classes.backgroundColor1} style={{ borderRadius: 4, padding: 8 }}>
              <div
                style={{
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: 14,
                }}
                dangerouslySetInnerHTML={{
                  __html: `${helpdesk.description}`.replace(/{UPLOAD_PATH}/g, `${api.getUri()}/static/uploads`),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={5} style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="overline" color="textPrimary" gutterBottom>
              ÚLTIMO TRÂMITE
            </Typography>
            <Box flexGrow={1} className={classes.backgroundColor1} style={{ padding: 8, borderRadius: 4 }}>
              {post ? (
                <Comment
                  userId={post?.owner}
                  userName={post?.owner_name}
                  userSector={post.owner_sector}
                  text={post?.text}
                  date={post?.created_at}
                  date_formated={post?.created_at_formated}
                  fullWidth
                  flip={post?.is_my}
                  system={post?.type >= 2}
                  readCheck
                  readed={post?.readed}
                  readed_date={post?.readed_at_formated}
                />
              ) : (
                <Box display="flex" alignItems="center" padding={2}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum trâmite registrado.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="info"
                component={Link}
                to={`/helpdesk/${helpdesk.id}`}
                target="_blank"
                disableElevation
              >
                ABRIR CHAMADO COMPLETO
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
});
HelpdeskAccordionItem.displayName = 'HelpdeskAccordionItem';
function RenderResult(props: {
  gettingHelpdeskData: boolean;
  classes: any;
  helpdeskData: HelpdeskData;
  globalStats: any;
  lastElementRef?: any;
  isFirstLoad: boolean;
  handleQuickSort: (orderBy: string) => void;
  currentOrderBy: string;
  currentOrderDir: OrderProp;
  applyFiltersFromDash: (type: string) => void;
  darkMode: boolean;
}) {
  const {
    gettingHelpdeskData,
    classes,
    helpdeskData,
    globalStats,
    lastElementRef,
    isFirstLoad,
    handleQuickSort,
    currentOrderBy,
    currentOrderDir,
    applyFiltersFromDash,
    darkMode,
  } = props;

  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);

  const handleAccordionToggle = useCallback((panelId: string, isExpanded: boolean) => {
    setExpandedAccordions(prev => (isExpanded ? [...prev, panelId] : prev.filter(id => id !== panelId)));
  }, []);

  const handleExpandAll = () => {
    if (helpdeskData?.data) {
      setExpandedAccordions(helpdeskData.data.map(h => h.id.toString()));
    }
  };

  const handleCollapseAll = () => setExpandedAccordions([]);

  const stats = globalStats || { pending: 0, in_progress_in_time: 0, in_progress_out_of_time: 0 };
  const totalOpen = stats.pending + stats.in_progress_in_time + stats.in_progress_out_of_time;

  const pctPending = totalOpen > 0 ? Math.round((stats.pending / totalOpen) * 100) : 0;
  const pctInTime = totalOpen > 0 ? Math.round((stats.in_progress_in_time / totalOpen) * 100) : 0;
  const pctDelayed = totalOpen > 0 ? Math.round((stats.in_progress_out_of_time / totalOpen) * 100) : 0;
  const showSkeletons = gettingHelpdeskData && isFirstLoad;
  return (
    <Box mt={2}>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.lotAccordionRoot}
              style={{ borderLeft: `4px solid ${darkMode ? blue[400] : blue[600]}`, height: '100%' }}
            >
              <CardActionArea onClick={() => applyFiltersFromDash('total')} style={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total em Aberto
                  </Typography>

                  <Typography variant="h4" color="textPrimary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={52}
                        height={28}
                        style={{ borderRadius: 4, marginTop: 12 }}
                      />
                    ) : (
                      totalOpen
                    )}
                  </Typography>
                  {showSkeletons ? (
                    <Skeleton variant="rectangular" width={52} height={18} style={{ borderRadius: 4, marginTop: 8 }} />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Visão Geral (100%)
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.lotAccordionRoot}
              style={{ borderLeft: `4px solid ${darkMode ? yellow[800] : yellow[600]}`, height: '100%' }}
            >
              <CardActionArea onClick={() => applyFiltersFromDash('pending')} style={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Pendentes (Não Iniciados)
                  </Typography>
                  <Typography variant="h4" color="textPrimary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={52}
                        height={28}
                        style={{ borderRadius: 4, marginTop: 12 }}
                      />
                    ) : (
                      stats.pending
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={62}
                        height={18}
                        style={{ borderRadius: 4, marginTop: 8 }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {pctPending}% do total
                      </Typography>
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.lotAccordionRoot}
              style={{ borderLeft: `4px solid ${darkMode ? green[500] : green[700]}`, height: '100%' }}
            >
              <CardActionArea onClick={() => applyFiltersFromDash('in_time')} style={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Em Andamento (No Prazo)
                  </Typography>
                  <Typography variant="h4" color="textPrimary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={52}
                        height={28}
                        style={{ borderRadius: 4, marginTop: 12 }}
                      />
                    ) : (
                      stats.in_progress_in_time
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={62}
                        height={18}
                        style={{ borderRadius: 4, marginTop: 8 }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {pctInTime}% do total
                      </Typography>
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              className={classes.lotAccordionRoot}
              style={{ borderLeft: `4px solid ${darkMode ? red[400] : red[700]}`, height: '100%' }}
            >
              <CardActionArea onClick={() => applyFiltersFromDash('delayed')} style={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Em Andamento (Atrasados)
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      color: stats.in_progress_out_of_time > 0 ? (darkMode ? red[400] : red[700]) : 'inherit',
                    }}
                  >
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={52}
                        height={28}
                        style={{ borderRadius: 4, marginTop: 12 }}
                      />
                    ) : (
                      stats.in_progress_out_of_time
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {showSkeletons ? (
                      <Skeleton
                        variant="rectangular"
                        width={62}
                        height={18}
                        style={{ borderRadius: 4, marginTop: 8 }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {pctDelayed}% do total
                      </Typography>
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={2}>
          <Box display="flex" gap={1}>
            <Tooltip title="Expandir Todos">
              <Button size="small" variant="outlined" color="inherit" onClick={handleExpandAll}>
                <UnfoldMoreIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Recolher Todos">
              <Button size="small" variant="outlined" color="inherit" onClick={handleCollapseAll}>
                <UnfoldLessIcon />
              </Button>
            </Tooltip>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="textSecondary">
              <SortIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} /> Ordenar por:
            </Typography>
            <Button
              size="small"
              variant={currentOrderBy === 'created_at' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handleQuickSort('created_at')}
              endIcon={
                currentOrderBy === 'created_at' ? (
                  currentOrderDir === 'asc' ? (
                    <ArrowUpwardIcon />
                  ) : (
                    <ArrowDownwardIcon />
                  )
                ) : undefined
              }
            >
              Abertura
            </Button>
            <Button
              size="small"
              variant={currentOrderBy === 'deadline' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handleQuickSort('deadline')}
              endIcon={
                currentOrderBy === 'deadline' ? (
                  currentOrderDir === 'asc' ? (
                    <ArrowUpwardIcon />
                  ) : (
                    <ArrowDownwardIcon />
                  )
                ) : undefined
              }
            >
              Vencimento
            </Button>
          </Box>
        </Box>
      </Box>

      {showSkeletons ? (
        <Box>
          {[...Array(6)]?.map((_, i) => (
            <HelpdeskSkeletonRow key={`skeleton-row-${i}`} />
          ))}
        </Box>
      ) : (
        <Box>
          {helpdeskData?.data?.length === 0 ? (
            <Box className={classes.gettingHelpdeskDataRoot}>
              <Typography>NENHUM HELPDESK ENCONTRADO</Typography>
            </Box>
          ) : (
            <Box>
              {helpdeskData?.data?.map((helpdesk: Helpdesk, index) => {
                const rowId = helpdesk.id.toString();
                const isLastElement = index === helpdeskData.data.length - 1;

                return (
                  <HelpdeskAccordionItem
                    key={rowId}
                    helpdesk={helpdesk}
                    expanded={expandedAccordions.includes(rowId)}
                    onToggle={handleAccordionToggle}
                    isLastElement={isLastElement}
                    lastElementRef={lastElementRef}
                    classes={classes}
                  />
                );
              })}

              {/* Skeletons extras no final caso esteja carregando a próxima página */}
              {gettingHelpdeskData && !isFirstLoad && (
                <Box mt={1}>
                  {[...Array(3)]?.map((_, i) => (
                    <HelpdeskSkeletonRow key={`extra-row-${i}`} />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function HelpdeskAttendancePage(props: HelpdeskAttendancePageProps) {
  const classes = styles();
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  moment.updateLocale(moment.locale(), { invalidDate: '00/00/0000 00:00' });
  const { search } = props;

  const [page, setPage] = useState<number>(1);
  const DATA_LIMIT = 24;

  const [gettingHelpdeskData, setGettingHelpdeskData] = useState<boolean>(false);
  const [helpdeskData, setHelpdeskData] = useState<HelpdeskData>({} as HelpdeskData);

  const [globalStats, setGlobalStats] = useState<any>(null);

  // Busca silenciosa apenas para alimentar os cards do topo de maneira imutável
  useEffect(() => {
    let active = true;
    api
      .get('helpdesk/', {
        params: { current_page: 1, data_limit: 1, situation: '0;1', delay: 0, attendance_only: true },
      })
      .then((res: any) => {
        if (active && res?.data?.statistics) {
          setGlobalStats(res.data.statistics);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const [searchSituations, setSearchSituations] = useState<any[]>(getInitialSituations());
  const [searchDelay, setSearchDelay] = useState<number>(
    Number(localStorage.getItem('search-helpdesk-attendance-delay') || 0),
  );
  const [searchAttendances, setSearchAttendances] = useState<ValueProps[]>(
    JSON.parse(localStorage.getItem('search-helpdesk-attendance-attendance') || '[]'),
  );
  const [searchOwners, setSearchOwners] = useState<ValueProps[]>(
    JSON.parse(localStorage.getItem('search-helpdesk-attendance-owners') || '[]'),
  );

  const [searchOrderBy, setSearchOrderBy] = useState<string>(
    localStorage.getItem('search-helpdesk-attendance-order-by') || 'created_at',
  );
  const [searchOrderDir, setSearchOrderDir] = useState<OrderProp>(
    (localStorage.getItem('search-helpdesk-attendance-order-dir') || 'asc') as OrderProp,
  );

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'info' | 'warning' | undefined>('success');
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: any) => {
      if (gettingHelpdeskData) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && helpdeskData.current_page < helpdeskData.total_page) {
          setPage(prevPage => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [gettingHelpdeskData, helpdeskData.current_page, helpdeskData.total_page],
  );

  const handleQuickSort = (orderBy: string) => {
    const newDir = searchOrderBy === orderBy && searchOrderDir === 'asc' ? 'desc' : 'asc';
    setSearchOrderBy(orderBy);
    setSearchOrderDir(newDir);
    localStorage.setItem('search-helpdesk-attendance-order-by', orderBy);
    localStorage.setItem('search-helpdesk-attendance-order-dir', newDir);
    setPage(1);
  };

  const getHelpdeskData = (
    {
      current_page = 1,
      order_by = searchOrderBy,
      order_dir = searchOrderDir,
      data_limit = DATA_LIMIT,
      owner = searchOwners,
      attendance = searchAttendances,
      situation = searchSituations,
      delay = searchDelay,
    }: HelpDeskSearchProps,
    isLoadMore = false,
  ) => {
    let cancel: Canceler;
    if (!gettingHelpdeskData) {
      const params = {
        current_page,
        data_limit,
        order_by,
        order_dir,
        owner: Array.isArray(owner) ? owner?.map((i: any) => i.id || i.value)?.join(';') : '',
        situation: Array.isArray(situation) ? situation?.map((i: any) => i.id || i.value)?.join(';') : '',
        attendance: Array.isArray(attendance) ? attendance?.map((i: any) => i.id || i.value)?.join(';') : '',
        delay,
        attendance_only: true,
      };

      setGettingHelpdeskData(true);

      api
        .get('helpdesk/', {
          params,
          cancelToken: new Axios.CancelToken(c => {
            cancel = c;
          }),
        })
        .then((response: any) => {
          const responseData = response?.data as HelpdeskData;

          if (isLoadMore) {
            responseData.data = [...(helpdeskData.data || []), ...responseData.data];
          }

          setHelpdeskData(responseData);
          setGettingHelpdeskData(false);
        })
        .catch((error: any) => {
          if (Axios.isCancel(error)) return;
          const msg = error?.response?.data?.error || error.message || 'Erro desconhecido, contate o TI.';
          setSnackbarType('error');
          setSnackbarMsg(msg);
          setOpenSnackbar(true);
          setGettingHelpdeskData(false);
        });
    }
    return () => cancel && cancel();
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const handleRefresh = () => {
    setPage(1);
    getHelpdeskData(
      {
        current_page: 1,
        order_by: searchOrderBy,
        order_dir: searchOrderDir,
        data_limit: DATA_LIMIT,
        owner: searchOwners,
        attendance: searchAttendances,
        situation: searchSituations,
        delay: searchDelay,
        ...search,
      },
      false,
    );
  };

  const applyFiltersFromDash = (type: string) => {
    let newSit: any[] = [];
    let newDel = 0;

    if (type === 'pending') {
      newSit = [{ id: '0', value: '0', text: 'Pendente' }];
      newDel = 0;
    } else if (type === 'in_time') {
      newSit = [{ id: '1', value: '1', text: 'Em andamento' }];
      newDel = 2;
    } else if (type === 'delayed') {
      newSit = [{ id: '1', value: '1', text: 'Em andamento' }];
      newDel = 1;
    } else if (type === 'total') {
      newSit = [
        { id: '0', value: '0', text: 'Pendente' },
        { id: '1', value: '1', text: 'Em andamento' },
      ];
      newDel = 0;
    }

    setSearchSituations(newSit);
    setSearchDelay(newDel);
    localStorage.setItem('search-helpdesk-attendance-situation', JSON.stringify(newSit));
    localStorage.setItem('search-helpdesk-attendance-delay', String(newDel));
    setPage(1);
  };

  const handleSituationChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const valArr = typeof value === 'string' ? value.split(',') : value;

    if (valArr.length === 0) {
      setSnackbarType('warning');
      setSnackbarMsg('O filtro de situação não pode ficar vazio nesta visualização.');
      setOpenSnackbar(true);
      return;
    }

    const newObjArr = valArr.map(v => ({
      id: v,
      value: v,
      text: ALLOWED_SITUATIONS.find(s => s.value === v)?.text || v,
    }));
    setSearchSituations(newObjArr);
    localStorage.setItem('search-helpdesk-attendance-situation', JSON.stringify(newObjArr));
    setPage(1);
  };

  const handleDelayChange = (event: SelectChangeEvent<number>) => {
    const val = Number(event.target.value);
    setSearchDelay(val);
    localStorage.setItem('search-helpdesk-attendance-delay', String(val));
    setPage(1);
  };

  useEffect(() => {
    let active = true;
    if (active) {
      getHelpdeskData(
        {
          ...search,
          order_by: searchOrderBy,
          order_dir: searchOrderDir,
          data_limit: DATA_LIMIT,
          owner: searchOwners,
          attendance: searchAttendances,
          situation: searchSituations,
          delay: searchDelay,
          current_page: page,
        },
        page > 1,
      );
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchSituations, searchDelay, searchOwners, searchAttendances, searchOrderBy, searchOrderDir]);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="flex-end" mb={2} flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Box minWidth={220}>
            <InputSearchAsync
              source="intranet-users"
              id="helpdesk-attendance-attendance"
              label="Atendente (+)"
              placeholder="Pesquisar Atendente"
              noOptionsText="Nenhum atendente encontrado."
              multiple
              value={searchAttendances}
              onChange={(val: any) => {
                setSearchAttendances(val);
                localStorage.setItem('search-helpdesk-attendance-attendance', JSON.stringify(val));
                setPage(1);
              }}
            />
          </Box>
          <Box minWidth={220}>
            <InputSearchAsync
              source="intranet-users"
              id="helpdesk-attendance-owner"
              label="Solicitante (+)"
              placeholder="Pesquisar Solicitante"
              noOptionsText="Nenhum solicitante encontrado."
              multiple
              value={searchOwners}
              onChange={(val: any) => {
                setSearchOwners(val);
                localStorage.setItem('search-helpdesk-attendance-owners', JSON.stringify(val));
                setPage(1);
              }}
            />
          </Box>
          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel id="situation-label">Situação</InputLabel>
            <Select
              labelId="situation-label"
              multiple
              value={searchSituations.map((s: any) => s.id || s.value)}
              onChange={handleSituationChange}
              input={<OutlinedInput label="Situação" />}
              className={classes.lotAccordionRoot}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => (
                    <Chip key={value} label={ALLOWED_SITUATIONS.find(s => s.value === value)?.text} size="small" />
                  ))}
                </Box>
              )}
            >
              {ALLOWED_SITUATIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" style={{ minWidth: 160 }}>
            <InputLabel id="delay-label">Prazo</InputLabel>
            <Select
              labelId="delay-label"
              value={searchDelay}
              onChange={handleDelayChange}
              input={<OutlinedInput label="Prazo" />}
              className={classes.lotAccordionRoot}
            >
              {DELAY_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Atualizar fila">
            <Button variant="outlined" color="primary" onClick={handleRefresh} style={{ height: 40 }}>
              <RefreshIcon />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.root}>
        <Grid container className={classes.contentRoot}>
          <Grid item xs={12}>
            <RenderResult
              gettingHelpdeskData={gettingHelpdeskData}
              classes={classes}
              helpdeskData={helpdeskData}
              globalStats={globalStats}
              lastElementRef={lastElementRef}
              isFirstLoad={page === 1}
              handleQuickSort={handleQuickSort}
              currentOrderBy={searchOrderBy}
              currentOrderDir={searchOrderDir}
              applyFiltersFromDash={applyFiltersFromDash}
              darkMode={darkMode}
            />
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert severity={snackbarType} onClose={handleSnackbarClose}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
